import { Model } from 'app/model/backendmodel';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { Metamodel } from 'app/model/base/model';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { CdmSettings } from 'app/model/dataflow/dataset-settings/cdm-settings';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { ParquetInlineSettings } from 'app/model/dataflow/dataset-settings/parquet-inline-settings';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import _ from 'lodash';
import { NamedAsyncAdapter } from '../named-async.adapter';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import {
  AdapterRegistry, AttributeMapping, AttributeReference, DataMapper, LinkedService,
  IColumnRelationshipInformation, IDenormalizeInfo, IDenormalizeEntity, IOriginDataTypeName, IMappingJoinPair, IMappingArtifactSource, IRelationship, ITable, IStorageDescriptor, ICdmSettings,
  ModelConstants, MappingDirective, MappingType, Owned, ValueAdapter
} from 'shell-models-lib';
import { DeltaSettings } from 'app/model/dataflow/dataset-settings/delta-dataset-settings';
import { CosmosSqlSettings } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { JsonSettings } from 'app/model/dataflow/dataset-settings/json-settings';
import { XmlSettings } from 'app/model/dataflow/dataset-settings/xml-settings';
import { OrcSettings } from 'app/model/dataflow/dataset-settings/orc-settings';
import { AvroSettings } from 'app/model/dataflow/dataset-settings/avro-settings';
import { SqlDBSettings } from 'app/model/dataflow/dataset-settings/sql-db-settings';
import { SynapseAnalyticsSettings } from 'app/model/dataflow/dataset-settings/synapse-analytics-settings';
import { OnPremiseSqlServerSettings } from 'app/model/dataflow/dataset-settings/on-premise-sql-server-settings';
import { SqlMISettings } from 'app/model/dataflow/dataset-settings/sql-mi-settings';
import { getQualifiedTypeString, escapeDelimiter } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { EventHubsSettings } from 'app/model/dataflow/dataset-settings/event-hubs-settings';
import { SnowflakeSettings } from 'app/model/dataflow/dataset-settings/snowflake-settings';
import { KafkaSettings } from 'app/model/dataflow/dataset-settings/kafka-settings';
import { CassandraSettings } from 'app/model/dataflow/dataset-settings/cassandra-settings';

@AdapterRegistry.adapter({
  model: Metamodel.modelFor(DataMapper),
  asynchronous: true,
  adapts: {
    PipelineName: '*',
    Sources: '*',
    Sinks: '*',
    MappingDirectives: '*'
  }
})
export class MappingArtifactAsyncAdapter extends NamedAsyncAdapter<DataMapper, Model.MappingArtifactResource | Model.MappingArtifact> {
  protected async _toResource(model: DataMapper): Promise<Model.MappingArtifactResource | Model.MappingArtifact> {
    const resource = await super._toResource(model) as Model.MappingArtifactResource;
    const linkedServiceReferenceType = 'LinkedServiceReference';
    resource.PipelineName = model.name;

    resource.Sources = [];
    if (model.sourceTables) {
      model.sourceTables.forEach((source) => {
        // TODO nimoolen: move to source/sink adapters
        resource.Sources.push({
          name: source.name,
          properties: {
            schema: source.storageDescriptor.columns.map((col) => {
              return {
                name: col.name,
                dataType: this._adaptType(col.originDataTypeName)
              };
            }),
            dslConnectorProperties: [
              {
                name: DslConnectorConstants.store,
                value: 'synapse'
              },
              {
                name: DslConnectorConstants.databaseType,
                value: 'spark'
              },
              {
                name: DslConnectorConstants.format,
                value: 'table'
              },
              {
                name: DslConnectorConstants.database,
                value: source.namespace ? source.namespace.databaseName : ''
              },
              {
                name: DslConnectorConstants.tableName,
                value: source.name
              }
            ]
          }
        });
      });
    }

    if (model.sourceDatasets) {
      model.sourceDatasets.forEach((source: DataFlowSource) => {
        let columns = source.columns;
        let dslConnectorObjects: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[] = [];
        const adfDataMapperSourceSupportedDatasetFormat: Set<DatasetFormatType> = new Set([DatasetFormatType.CosmosDB,
          DatasetFormatType.Json,
          DatasetFormatType.Xml,
          DatasetFormatType.Orc,
          DatasetFormatType.Avro,
          DatasetFormatType.SqlDB,
          DatasetFormatType.CDM,
          DatasetFormatType.Delimited,
          DatasetFormatType.Parquet,
          DatasetFormatType.SqlMI,
          DatasetFormatType.OnPremiseSqlServer,
          DatasetFormatType.EventHubs,
          DatasetFormatType.Kafka,
          DatasetFormatType.Snowflake]);
        const adfDataMapperSourceSupportedFileDatasetFormat: Set<DatasetFormatType> = new Set([
          DatasetFormatType.Json,
          DatasetFormatType.Xml,
          DatasetFormatType.Orc,
          DatasetFormatType.Avro,
          DatasetFormatType.CDM,
          DatasetFormatType.Delimited,
          DatasetFormatType.Parquet]);

        if (!__IS_A365__ && !columns && !!source.schema) {
          columns = source.schema;
        }

        if(!__IS_A365__){
          if(adfDataMapperSourceSupportedDatasetFormat.has(source.datasetSettings.getFormat())){
            dslConnectorObjects = this._getDslConnectorProperties(source);

            if(adfDataMapperSourceSupportedFileDatasetFormat.has(source.datasetSettings.getFormat())){
              dslConnectorObjects.push({
                name: DslConnectorConstants.dateFormats,
                value: DslConnectorConstants.dateFormatOptions
              });
              dslConnectorObjects.push({
                name: DslConnectorConstants.timestampFormats,
                value: DslConnectorConstants.timestampFormatOptions.map((v)=>DataFlowExpressionUtils.escapeSingleQuoteInDslString(v))
              });
            }

          }
        } else if ((source.datasetSettings as DelimitedTextSettings).columnDelimiter ||
            source.datasetSettings.getFormat() === DatasetFormatType.Parquet ||
            source.datasetSettings.getFormat() === DatasetFormatType.CDM) {
          dslConnectorObjects = this._getDslConnectorProperties(source);
        }

        // TODO nimoolen: move to source/sink adapters
        resource.Sources.push({
          name: source.name,
          properties: {
            schema: columns.map((col) => {
              return {
                name: col.name,
                dataType: getQualifiedTypeString(col)
              };
            }),
            dslConnectorProperties: dslConnectorObjects,
            linkedService: {
              referenceName: source.sourceLinkedService.name,
              type: linkedServiceReferenceType
            }
          }
        });
      });
    }

    resource.Sinks = [];

    if (!__IS_A365__) {
      model.targetDatasets.forEach((target: DataFlowSource) => {
        let columns = target.columns;
        let linkedServiceName = target.schemaLinkedService?.name;

        if (!__IS_A365__) {
          if (!columns && !!target.schema) {
            columns = target.schema;
          }

          if (!linkedServiceName) {
            linkedServiceName = target.sourceLinkedService?.name;
          }
        }

        if (target.sourceDataset !== null) {
          let dslConnectorObjects: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[] = [];
          const adfDataMapperSinkSupportedDatasetFormat: Set<DatasetFormatType> = new Set([
            DatasetFormatType.Json,
            DatasetFormatType.Orc,
            DatasetFormatType.Avro,
            DatasetFormatType.CDM,
            DatasetFormatType.Delimited,
            DatasetFormatType.Parquet,
            DatasetFormatType.SynapseAnalytics,
            DatasetFormatType.Delta,
            DatasetFormatType.SqlDB,
            DatasetFormatType.SqlMI,
            DatasetFormatType.EventHubs]);

          if(adfDataMapperSinkSupportedDatasetFormat.has(target.datasetSettings.getFormat())){
            dslConnectorObjects = this._getDslConnectorProperties(target);
          }

          const targetProperties: Model.MappingArtifactSourceSinkPropertiesResource = {
            schema: columns.map((col) => {
              return {
                name: col.name,
                dataType: getQualifiedTypeString(col)
              };
            }),
            dslConnectorProperties: dslConnectorObjects,
            linkedService: {
              referenceName: linkedServiceName,
              type: linkedServiceReferenceType
            }
          };
          if (__IS_A365__){
            targetProperties.dataset = {
              referenceName: target.sourceDataset.name,
              type: DslConnectorConstants.datasetReference
            };
          }

          resource.Sinks.push({
            name: target.name,
            properties: targetProperties
          });

        }
      });
    }

    model.targetTables.forEach((sink) => {
      // TODO nimoolen: move to source/sink adapters
      resource.Sinks.push({
        name: sink.name,
        properties: {
          schema: sink.storageDescriptor.columns.map((col) => {
            return {
              name: col.name,
              dataType: this._adaptType(col.originDataTypeName)
            };
          }),
          dslConnectorProperties: [
            {
              name: DslConnectorConstants.store,
              value: 'synapse'
            },
            {
              name: DslConnectorConstants.databaseType,
              value: 'spark'
            },
            {
              name: DslConnectorConstants.format,
              value: 'table'
            },
            {
              name: DslConnectorConstants.database,
              value: sink.namespace ? sink.namespace.databaseName : ''
            },
            {
              name: DslConnectorConstants.tableName,
              value: sink.name
            }
          ]
        }
      });
    });

    resource.SinkEntityRelationships = this._toResourceRelationships(model.targetRelationships);

    // TODO nimoolen - replace with actual adapter
    resource.MappingDirectives =
        model.mappingDirectives.map((mappingDirective) => {
          const sourceDenormInfo = !!mappingDirective.sourceDenormalizeInfo && !!mappingDirective.sourceDenormalizeInfo.entities && mappingDirective.sourceDenormalizeInfo.entities.length
              ? mappingDirective.sourceDenormalizeInfo
              : undefined;

          return !mappingDirective.sourceEntityReferences.length || !mappingDirective.attributeMappingInfo.length ? undefined : {
            targetEntityReference: {
              name: mappingDirective.targetEntityReference.name
            },
            sourceDenormalizeInfo: sourceDenormInfo,
            sourceEntityReferences: mappingDirective.sourceEntityReferences.map((sourceRef) => {
              return {
                name: sourceRef.name
              };
            }),
            attributeMappingInfo: {
              attributeMappings: mappingDirective.attributeMappingInfo.map((attributeMapping) => this._mappingsToResource(attributeMapping))
            }
          };
        }).filter((mapping) => !!mapping);

    if (model.raw) {
      const parsed = JSON.parse(model.raw);
      _.merge(resource, parsed);
    }
    return resource;
  }

  protected async _toModel(resource: Model.MappingArtifact, model?: DataMapper): Promise<DataMapper> {
    if (!model) {
      model = DataMapper.MODEL.create<DataMapper>();
    }

    await super._toModel(resource.properties, model);

    model.raw = JSON.stringify(await ValueAdapter.remainder(this.context, resource.properties, DataMapper.MODEL));
    this._parseJSONToModel(resource.name, resource.properties, model);

    return model;
  }

  private _parseJSONToModel(name: string, resource: Model.MappingArtifactResource, model?: DataMapper): DataMapper {
    if (!resource) {
      return model;
    }

    model.name = name;

    if (!!resource.Sources) {
      if (!!model.sourceTables) {
        model.sourceTables = [];
      }

      if (!!model.sourceDatasets) {
        model.sourceDatasets = [];
      }

      for (const source of resource.Sources) {
        if (this._isSynapseTableSource(source)) {
          model.sourceTables.push(this._toModelTable(source));
        }
        else {
          model.sourceDatasets.push(this._toModelSourceDatasets(source, model));
        }
      }

      if (model.sourceTables.length === 0) {
        model.sourceTables = null;
      }

      if (model.sourceDatasets.length === 0) {
        model.sourceDatasets = null;
      }
    }

    model.sourceLinkedService = !!resource.Sources && resource.Sources.length > 0 ? this._getLinkedService(resource.Sources[0]) : null;
    model.targetTables = !!resource.Sinks ? resource.Sinks.map((s) => this._toModelTable(s)) : null;
    model.targetRelationships = !!resource.SinkEntityRelationships ? resource.SinkEntityRelationships.map((rel) => this._toModelRelationship(rel)) : null;
    model.mappingDirectives = !!resource.MappingDirectives ? resource.MappingDirectives.map((md) => this._toModelMapping(md)) : null;

    return model;
  }

  private _isSynapseTableSource(sourceOrSink: Model.MappingArtifactSourceSinkResource): boolean {
    if (sourceOrSink && sourceOrSink.properties && sourceOrSink.properties.dslConnectorProperties) {
      for (const nameValuePair of sourceOrSink.properties.dslConnectorProperties) {
        if (nameValuePair.name === 'format') {
          return nameValuePair.value === 'table';
        }
      }
    }

    return false;
  }

  private _toModelMapping(mappingResource: Model.MappingArtifactMappingDirectiveResource): MappingDirective {
    if (!mappingResource) {
      return null;
    }

    const mappingDirective: MappingDirective = MappingDirective.MODEL.create();

    mappingDirective.targetEntityReference = this._toModelAttributeReference(mappingResource.targetEntityReference);
    mappingDirective.sourceEntityReferences = !!mappingResource.sourceEntityReferences ? mappingResource.sourceEntityReferences.map((sourceRef) => this._toModelAttributeReference(sourceRef)) : null;
    mappingDirective.sourceDenormalizeInfo = this._toModelDenormalizationInfo(mappingResource.sourceDenormalizeInfo);
    mappingDirective.attributeMappingInfo = !!mappingResource.attributeMappingInfo && mappingResource.attributeMappingInfo.attributeMappings
        ? mappingResource.attributeMappingInfo.attributeMappings.map((am) => this._toModelAttributeMapingInfo(am, mappingResource.targetEntityReference))
        : null;

    return mappingDirective;
  }

  private _toModelAttributeMapingInfo(
      attrMappingResource: Model.MappingArtifactMappingDirectiveAttributeMappingResource, targetEntityReference: Model.MappingArtifactMappingDirectiveEntityReferenceResource): AttributeMapping {
    if (!attrMappingResource) {
      return null;
    }

    const attrMappingModel: AttributeMapping = new AttributeMapping(attrMappingResource.name);

    // TODO: This could be an issue for well known derived types.
    // Comparision of expression might be needed to map it back correctly.
    attrMappingModel.mappingType = attrMappingResource.type === MappingType.DERIVED ? MappingType.ADVANCED : attrMappingResource.type;
    attrMappingModel.attributeReference = this._toModelAttributeReference(attrMappingResource.attributeReference);
    attrMappingModel.attributeReferences = !!attrMappingResource.attributeReferences ? attrMappingResource.attributeReferences.map((ar) => this._toModelAttributeReference(ar)) : null;

    // TODO: Check if there loss of data in ths conversion.
    attrMappingModel.expression =  new DataFlowExpression();
    attrMappingModel.expression.expression = attrMappingResource.expression;

    attrMappingModel.alternateKeys = !!attrMappingResource.alternateKeys ? attrMappingResource.alternateKeys.map((ak) => ak) : null;
    attrMappingModel.lookupFrom = attrMappingResource.lookupFrom;
    attrMappingModel.unpivotInfo = attrMappingResource.unpivotInfo;

    return attrMappingModel;
  }

  private _toModelDenormalizationInfo(resourceDenormInfo: Model.MappingArtifactMappingDirectiveDenormalizationInfo): IDenormalizeInfo {
    if (!resourceDenormInfo || !resourceDenormInfo.entities) {
      const modelDenormInfo: IDenormalizeInfo = {
        entities: []
      };

      return modelDenormInfo;
    }

    const modelDenormInfo: IDenormalizeInfo = {
      entities: resourceDenormInfo.entities.map((re) => this._toModelDenormalizeEntity(re))
    };

    return modelDenormInfo;
  }

  private _toModelDenormalizeEntity(denormEntity: Model.MappingArtifactMappingDirectiveDenormalizationEntity): IDenormalizeEntity {
    if (!denormEntity) {
      return null;
    }

    const entity: IDenormalizeEntity = {
      toEntity: denormEntity.toEntity,
      joinPairs: !!denormEntity.joinPairs ? denormEntity.joinPairs.map((jp) => this._toModelJoinPair(jp)) : null
    };

    return entity;
  }

  private _toModelJoinPair(joinPair: Model.MappingArtifactJoinPair): IMappingJoinPair {
    if (!joinPair) {
      return null;
    }

    const modelJoinPair: IMappingJoinPair = {
      toAttribute: joinPair.toAttribute,
      operator: joinPair.operator,
      expression: joinPair.expression,
      fromAttribute: this._toModelAttributeReference(joinPair.fromAttribute)
    };

    return modelJoinPair;
  }

  private _toModelAttributeReference(resourceEntityReference: Model.MappingArtifactMappingDirectiveEntityReferenceResource): AttributeReference {
    if (!resourceEntityReference) {
      return null;
    }

    const modelAttributeReference: AttributeReference = {
      name: resourceEntityReference.name,
      entity: resourceEntityReference.entity
    };

    return modelAttributeReference;
  }

  private _toModelTable(sourceOrSink: Model.MappingArtifactSourceSinkResource): ITable {
    if (!sourceOrSink) {
      return null;
    }

    const dslProperties = !!sourceOrSink.properties && !!sourceOrSink.properties.dslConnectorProperties ? sourceOrSink.properties.dslConnectorProperties : null;
    const table: ITable = {
      name: sourceOrSink.name,
      storageDescriptor: this._toModelStorageDescriptor(sourceOrSink),
      namespace: { databaseName: this._getDslPropertyValue(dslProperties, DslConnectorConstants.database) }
    };

    return table;
  }

  private _toModelSourceDatasets(source: Model.MappingArtifactSourceSinkResource, model: DataMapper): DataFlowSource {
    if (!source) {
      return null;
    }

    const sourceDataset: DataFlowSource = Owned.create<DataFlowSource>(DataFlowSource.MODEL, model);
    const dslConnectorProperties = !!source.properties && !!source.properties.dslConnectorProperties ? source.properties.dslConnectorProperties : null;
    const formatValue = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.format);

    sourceDataset.name = source.name;
    if (!!source.properties && !!source.properties.schema) {
      source.properties.schema.forEach((col) => {
        const dataflowColumn: DataFlowColumn = DataFlowColumn.MODEL.create();
        dataflowColumn.name = col.name;
        dataflowColumn.columnType = col.dataType;

        sourceDataset.schema.push(dataflowColumn);
      });
    }

    sourceDataset.sourceLinkedService = this._getLinkedService(source);
    sourceDataset.isInlineDataset = true;

    if (!dslConnectorProperties) {
      return sourceDataset;
    }

    if (formatValue === DatasetFormatType.Delimited) {
      sourceDataset.datasetSettings = this._toModelDelimitedTextSettings(dslConnectorProperties, sourceDataset);
    } else if (formatValue === DatasetFormatType.Parquet) {
      sourceDataset.datasetSettings = this._toModelParquetInlineSettings(dslConnectorProperties, sourceDataset);
    } else {
      sourceDataset.datasetSettings = this._toModelCdmSettings(dslConnectorProperties);
    }

    return sourceDataset;
  }

  private _toModelDelimitedTextSettings(dslConnectorProperties: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[], sourceDataset: IMappingArtifactSource): DelimitedTextSettings {
    // Read dsl properties

    const columnDelimiter = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.columnDelimiter);
    const columnNamesAsHeader = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.columnNamesAsHeader);
    const compressionLevel = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionLevel);
    const encoding = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.encoding);
    const escapeChar = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.escapeChar);
    const fileName = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileName);
    const fileMode = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileMode);
    const fileSystem = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileSystem);
    const folderPath = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
    const nullValue = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.nullValue);
    const quoteChar = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.quoteChar);
    const rowDelimiter = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.rowDelimiter);
    const wildcardPaths = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.wildcardPaths);

    // Create and populate DelimitedTextSettings
    const delimitedSettings: DelimitedTextSettings = Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, sourceDataset as DataFlowSource);
    delimitedSettings.initialize(undefined);

    sourceDataset.datasetType = ModelConstants.delimitedTextDataset;
    if (wildcardPaths && wildcardPaths.length > 0) {
      sourceDataset.wildcardPaths = wildcardPaths.map((wp: string) => {
        const dfe = new DataFlowExpression();
        return dfe.expression = wp;
      });
    }

    delimitedSettings.compressionType = undefined;
    delimitedSettings.columnDelimiter = createDataflowExpression(columnDelimiter) as DataFlowExpression;
    delimitedSettings.columnNamesAsHeader = createDataflowExpression(columnNamesAsHeader) as DataFlowExpression;
    delimitedSettings.compressionLevel = createDataflowExpression(compressionLevel) as DataFlowExpression;
    delimitedSettings.container = createDataflowExpression(fileSystem) as DataFlowExpression;
    delimitedSettings.encoding = createDataflowExpression(encoding) as DataFlowExpression;
    delimitedSettings.escapeCharacter = createDataflowExpression(escapeChar) as DataFlowExpression;
    delimitedSettings.fileName = createDataflowExpression(fileName) as DataFlowExpression;
    delimitedSettings.fileMode = fileMode;
    delimitedSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
    delimitedSettings.nullValue = createDataflowExpression(nullValue) as DataFlowExpression;
    delimitedSettings.quoteCharacter = createDataflowExpression(quoteChar) as DataFlowExpression;
    delimitedSettings.rowDelimiter = createDataflowExpression(rowDelimiter) as DataFlowExpression;

    return delimitedSettings;
  }

  private _toModelParquetInlineSettings(dslConnectorProperties: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[], sourceDataset: DataFlowSource): ParquetInlineSettings {
    // Read dsl properties
    const compressionCodec = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);
    const fileName = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileName);
    const fileMode = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileMode);
    const fileSystem = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileSystem);
    const folderPath = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
    const wildcardPaths = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.wildcardPaths);

    // Create and populate ParquetInlineSettings
    const parquetSettings = Owned.create<ParquetInlineSettings>(ParquetInlineSettings.MODEL, sourceDataset);
    parquetSettings.initialize(undefined);

    if (wildcardPaths && wildcardPaths.length > 0) {
      sourceDataset.wildcardPaths = wildcardPaths.map((wp: string) => createDataflowExpression(wp) as DataFlowExpression);
    }

    parquetSettings.compressionType = compressionCodec;
    parquetSettings.container = createDataflowExpression(fileSystem) as DataFlowExpression;
    parquetSettings.fileName = createDataflowExpression(fileName) as DataFlowExpression;
    parquetSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
    parquetSettings.fileMode = fileMode;

    return parquetSettings;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _toModelCdmSettings(dslConnectorProperties: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[]): any {
    // Read dsl properties
    const entity = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.entity);
    const fileSystem = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileSystem);
    const fileName = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileName);
    const folderPath = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
    const manifestName = this._getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.manifestName);

    // Create and populate CdmSettings
    const cmdSettings: ICdmSettings = {
      entity: createDataflowExpression(entity),
      manifestName: createDataflowExpression(manifestName),
      subFormatSettings: {
        folderPath: createDataflowExpression(folderPath),
        container: createDataflowExpression(fileSystem),
        fileName: createDataflowExpression(fileName),
        fileMode: 0
      }
    };

    return cmdSettings;
  }

  private _toModelStorageDescriptor(source: Model.MappingArtifactSourceSinkResource): IStorageDescriptor {
    if (!source || !source.properties || !source.properties.schema) {
      return null;
    }

    const storageDescriptor: IStorageDescriptor = {
      columns: source.properties.schema.map((col) => {
        return { name: col.name, originDataTypeName: this._toModelDataType(col.dataType) };
      })
    };

    return storageDescriptor;
  }

  private _toResourceRelationships(relationships: IRelationship[]): Model.MappingArtifactEntityRelationship[] {
    return !!relationships && relationships.length > 0
        ? relationships.map((rel) => this._toResourceRelationship(rel))
        : [];
  }

  private _toResourceRelationship(relationship: IRelationship): Model.MappingArtifactEntityRelationship {
    if (!relationship) {
      return null;
    }

    return {
      fromEntity: relationship.fromTableName,
      toEntity: relationship.toTableName,
      attributePairs: this._toResourceAttributePairsList(relationship.columnRelationshipInformations)
    };
  }

  private _toModelRelationship(relationship: Model.MappingArtifactEntityRelationship): IRelationship {
    if (!relationship) {
      return null;
    }

    return {
      fromTableName: relationship.fromEntity,
      toTableName: relationship.toEntity,
      columnRelationshipInformations: !!relationship.attributePairs ? relationship.attributePairs.map((colRel) => this._toModelColumnRelationshipInformation(colRel)) : [],
      name: undefined,
      namespace: undefined,
      relationshipType: undefined
    };
  }

  private _toResourceAttributePairsList(columnRelationships: IColumnRelationshipInformation[]): Model.MappingArtifactAttributePairs[] {
    return !!columnRelationships && columnRelationships.length > 0
        ? columnRelationships.map((colRel) => this._toResourceAttributePairs(colRel))
        : [];
  }

  private _toResourceAttributePairs(columnRelationshipInformation: IColumnRelationshipInformation): Model.MappingArtifactAttributePairs {
    if (!columnRelationshipInformation) {
      return null;
    }

    return {
      fromAttribute: columnRelationshipInformation.fromColumnName,
      toAttribute: columnRelationshipInformation.toColumnName
    };
  }

  private _toModelColumnRelationshipInformation(attributePairs: Model.MappingArtifactAttributePairs): IColumnRelationshipInformation {
    if (!attributePairs) {
      return null;
    }

    return {
      fromColumnName: attributePairs.fromAttribute,
      toColumnName: attributePairs.toAttribute
    };
  }

  private _mappingsToResource(inputMapping: AttributeMapping): Model.MappingArtifactMappingDirectiveAttributeMappingResource {
    const mappingType = inputMapping.mappingType;
    const attributeMapping: Model.MappingArtifactMappingDirectiveAttributeMappingResource = {
      name: inputMapping.name,
      type: inputMapping.mappingType === MappingType.ADVANCED ? MappingType.DERIVED : inputMapping.mappingType
    };

    switch (mappingType) {
      case MappingType.ADVANCED:
        if (inputMapping.expression && !!inputMapping.expression.expression) {
          attributeMapping.expression = inputMapping.expression.expression;
          // TODO nimoolen - fix this
          attributeMapping.attributeReferences = inputMapping.attributeReferences.map((ref) => {
            return {
              name: ref.name,
              entity: ref.entity
            };
          });
        }
        break;
      case MappingType.AGGREGATE:
      case MappingType.DERIVED:
        attributeMapping.expression = `${inputMapping.functionName}(${inputMapping.attributeReference.name})`;
        attributeMapping.attributeReferences = [{
          name: inputMapping.attributeReference.name,
          entity: inputMapping.attributeReference.entity
        }];
        break;
      case MappingType.SURROGATEKEY:
        attributeMapping.alternateKeys = inputMapping.alternateKeys;
        break;
      case MappingType.LOOKUP:
        attributeMapping.lookupFrom = inputMapping.lookupFrom;
        break;
      case MappingType.UNPIVOT:
        attributeMapping.unpivotInfo = inputMapping.unpivotInfo;
        break;
      default:
        if (inputMapping.attributeReference && !!inputMapping.attributeReference.name) {
          attributeMapping.attributeReference = {
            name: inputMapping.attributeReference.name,
            entity: inputMapping.attributeReference.entity
          };
        }
        break;
    }
    return attributeMapping;
  }

  private _adaptType(sparkType: IOriginDataTypeName): string {
    if (sparkType.typeName === 'decimal') {
      return `${sparkType.typeName}(${sparkType.precision},${sparkType.scale})`;
    }
    return sparkType.typeName;
  }

  private _toModelDataType(dataType: string): IOriginDataTypeName {
    if (!dataType || dataType.length === 0) {
      return null;
    }

    // TODO: see if information around isComplexType or isNullable needs to be saved on the server side
    const originalDataType: IOriginDataTypeName = {
      typeName: dataType,
      isComplexType: false,
      isNullable: false
    };
    if (dataType.startsWith('decimal')) {
      try {
        // dataType is of the form: decimal(precision, scale)
        let str = dataType.substring('decimal'.length); // yeilds: "(precision, scale)"
        str = str.substring(1, dataType.length - 2); // yeilds: "precision, scale"
        const parts = str.split(',');
        const precision = parts[0].trim() || '';
        const scale = parts[1].trim() || '';

        originalDataType.typeName = 'decimal';
        originalDataType.precision =  parseInt(precision, 10);
        originalDataType.scale = parseInt(scale, 10);
      } catch (error) {
        throw new Error('Invalid decimal data type format encountered while converting DataMapper resource to model. Error: ' + error);
      }
    }

    return originalDataType;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _getDslPropertyValue(dslConnectorProperties: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[], propertyName: string): any {
    if (!dslConnectorProperties) {
      return null;
    }

    const matchingProperty = dslConnectorProperties.find((cp) => cp.name === propertyName);
    return !!matchingProperty ? matchingProperty.value : null;
  }

  private _getLinkedService(source: Model.MappingArtifactSourceSinkResource): LinkedService {
    if (source && source.properties && source.properties.linkedService) {
      return ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(LinkedService), source.properties.linkedService, this.context);
    }

    return null;
  }

  private _getDslConnectorProperties(source: DataFlowSource): Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[] {
    const dslConnectorObjects: Model.MappingArtifactSourceSinkDslConnectorPropertiesResource[] = [];
    if ((source.datasetSettings as DelimitedTextSettings).columnDelimiter){
      const datasetSettings = source.datasetSettings as DelimitedTextSettings;
      const wildcardPaths = source.wildcardPaths.filter((pt)=> pt !== undefined && pt.expression !== undefined);
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Delimited
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.container.expression
      });
      if (wildcardPaths && wildcardPaths.length > 0) {
        const paths: string[] = [];
        wildcardPaths.map((pt) => {
          paths.push(pt.expression);
        });
        dslConnectorObjects.push({
          name: DslConnectorConstants.wildcardPaths,
          value: paths
        });
      }
      if ((!wildcardPaths || !wildcardPaths.length)){
        if(datasetSettings.fileName && datasetSettings.fileName.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileName,
            value: datasetSettings.fileName.expression
          });}
        if(datasetSettings.folderPath && datasetSettings.folderPath.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.folderPath,
            value: datasetSettings.folderPath.expression
          });}
      }
      if (datasetSettings.columnDelimiter && datasetSettings.columnDelimiter.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.columnDelimiter,
          value: escapeDelimiter(datasetSettings.columnDelimiter.expression)
        });
      }
      if (datasetSettings.rowDelimiter && datasetSettings.rowDelimiter.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.rowDelimiter,
          value: escapeDelimiter(datasetSettings.rowDelimiter.expression)
        });
      }
      if (datasetSettings.columnNamesAsHeader && datasetSettings.columnNamesAsHeader.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.columnNamesAsHeader,
          value: datasetSettings.columnNamesAsHeader.expression
        });
      }
      if (datasetSettings.escapeCharacter && datasetSettings.escapeCharacter.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.escapeChar,
          value: escapeDelimiter(datasetSettings.escapeCharacter.expression)
        });
      }
      if (datasetSettings.encoding && datasetSettings.encoding.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.encoding,
          value: datasetSettings.encoding.expression
        });
      }
      if (datasetSettings.fileMode) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.fileMode,
          value: datasetSettings.fileMode
        });
      }
      if (datasetSettings.quoteCharacter && datasetSettings.quoteCharacter.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.quoteChar,
          value: escapeDelimiter(datasetSettings.quoteCharacter.expression)
        });
      }
      if (datasetSettings.compressionLevel && datasetSettings.compressionLevel.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.compressionLevel,
          value: datasetSettings.compressionLevel.expression
        });
      }
      if (datasetSettings.nullValue && datasetSettings.nullValue.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.nullValue,
          value: datasetSettings.nullValue.expression
        });
      }
    } else if ((source.datasetSettings as ParquetInlineSettings).getFormat() === DatasetFormatType.Parquet){
      const datasetSettings = source.datasetSettings as ParquetInlineSettings;
      const wildcardPaths = source.wildcardPaths.filter((pt)=> pt && pt.expression);
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Parquet
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.container.expression
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.compressionCodec,
        value: datasetSettings.compressionType ? datasetSettings.compressionType : 'snappy'
      });
      if (wildcardPaths && wildcardPaths.length > 0) {
        const paths: string[] = [];
        source.wildcardPaths.map((pt) => {
          paths.push(pt.expression);
        });
        dslConnectorObjects.push({
          name: DslConnectorConstants.wildcardPaths,
          value: paths
        });
      }
      if ((!(wildcardPaths) || !wildcardPaths.length)) {
        if(datasetSettings.fileName && datasetSettings.fileName.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileName,
            value: datasetSettings.fileName.expression
          });
        }
        if(datasetSettings.folderPath && datasetSettings.folderPath.expression) {
          dslConnectorObjects.push({
            name: DslConnectorConstants.folderPath,
            value: datasetSettings.folderPath.expression
          });
        }
        if(datasetSettings.fileMode) {
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileMode,
            value: datasetSettings.fileMode
          });
        }}
    } else if ((source.datasetSettings as CdmSettings).getFormat() === DatasetFormatType.CDM){
      const datasetSettings = source.datasetSettings as CdmSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.CDM
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.subFormatSettings.container.expression
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.entity,
        value: datasetSettings.entity.expression
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.manifestType,
        value: 'manifest'
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.manifestName,
        value: datasetSettings.manifestName.expression
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.local,
        value: true
      });
      if (datasetSettings.subFormatSettings.folderPath && datasetSettings.subFormatSettings.folderPath.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.folderPath,
          value: datasetSettings.subFormatSettings.folderPath.expression
        });
      }
    } else if ((source.datasetSettings as CosmosSqlSettings).getFormat() === DatasetFormatType.CosmosDB){
      const datasetSettings = source.datasetSettings as CosmosSqlSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.CosmosDB
      });

      if (datasetSettings.tableName && datasetSettings.tableName.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.container,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.tableName.expression)
        });
      }
    } else if((source.datasetSettings as SqlDBSettings).getFormat() === DatasetFormatType.SqlDB){
      const datasetSettings = source.datasetSettings as SqlDBSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.SqlDB
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: datasetSettings.tableName.expression
        });
      }
      if(datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaName,
          value: datasetSettings.schemaName.expression
        });
      }
    } else if ((source.datasetSettings as CassandraSettings).getFormat() === DatasetFormatType.Cassandra){
      const datasetSettings = source.datasetSettings as CassandraSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.Cassandra
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: datasetSettings.tableName.expression
        });
      }
      if (datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.keyspace,
          value: datasetSettings.schemaName.expression
        });
      }
    } else if((source.datasetSettings as AvroSettings).getFormat() === DatasetFormatType.Avro){
      const datasetSettings = source.datasetSettings as AvroSettings;
      const wildcardPaths = source.wildcardPaths.filter((pt)=> pt && pt.expression);
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Avro
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.container.expression
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.compressionCodec,
        value: datasetSettings.compressionType ? datasetSettings.compressionType : 'snappy'
      });
      if (wildcardPaths && wildcardPaths.length > 0){
        const paths: string[] = [];
        wildcardPaths.map((pt) => {
          paths.push(pt.expression);
        });
        dslConnectorObjects.push({
          name: DslConnectorConstants.wildcardPaths,
          value: paths
        });
      }
      if ((!wildcardPaths || !wildcardPaths.length)) {
        if(datasetSettings.folderPath && datasetSettings.folderPath.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.folderPath,
            value: datasetSettings.folderPath.expression
          });
        }
        if(datasetSettings.fileMode) {
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileMode,
            value: datasetSettings.fileMode
          });
        }
      }
    } else if ((source.datasetSettings as OrcSettings).getFormat() === DatasetFormatType.Orc){
      const datasetSettings = source.datasetSettings as OrcSettings;
      const wildcardPaths = source.wildcardPaths.filter((pt)=> pt && pt.expression);
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Orc
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.container.expression
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.compressionCodec,
        value: datasetSettings.compressionType ? datasetSettings.compressionType : 'snappy'
      });
      if (wildcardPaths && wildcardPaths.length > 0){
        const paths: string[] = [];
        wildcardPaths.map((pt) => {
          paths.push(pt.expression);
        });
        dslConnectorObjects.push({
          name: DslConnectorConstants.wildcardPaths,
          value: paths
        });
      }
      if ((!wildcardPaths || !wildcardPaths.length)) {
        if(datasetSettings.folderPath && datasetSettings.folderPath.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.folderPath,
            value: datasetSettings.folderPath.expression
          });
        }
        if(datasetSettings.fileMode) {
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileMode,
            value: datasetSettings.fileMode
          });
        }
      }
    } else if ((source.datasetSettings as XmlSettings).getFormat() === DatasetFormatType.Xml){
      const datasetSettings = source.datasetSettings as XmlSettings;
      const wildcardPaths = source.wildcardPaths.filter((pt)=> pt && pt.expression);
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Xml
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.container.expression
      });
      if (wildcardPaths && wildcardPaths.length > 0){
        const paths: string[] = [];
        wildcardPaths.map((pt) => {
          paths.push(pt.expression);
        });
        dslConnectorObjects.push({
          name: DslConnectorConstants.wildcardPaths,
          value: paths
        });
      }
      if ((!(wildcardPaths) || !wildcardPaths.length)) {
        if(datasetSettings.fileName && datasetSettings.fileName.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileName,
            value: datasetSettings.fileName.expression
          });
        }
        if(datasetSettings.fileMode){
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileMode,
            value: datasetSettings.fileMode
          });
        }
        if (datasetSettings.folderPath && datasetSettings.folderPath.expression) {
          dslConnectorObjects.push({
            name: DslConnectorConstants.folderPath,
            value: datasetSettings.folderPath.expression
          });
        }
      }
    } else if ((source.datasetSettings as JsonSettings).getFormat() === DatasetFormatType.Json){
      const datasetSettings = source.datasetSettings as JsonSettings;
      const wildcardPaths = source.wildcardPaths.filter((pt)=> pt && pt.expression);
      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Json
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: datasetSettings.container.expression
      });
      if (wildcardPaths && wildcardPaths.length > 0){
        const paths: string[] = [];
        wildcardPaths.map((pt) => {
          paths.push(pt.expression);
        });
        dslConnectorObjects.push({
          name: DslConnectorConstants.wildcardPaths,
          value: paths
        });
      }
      if ((!(source.wildcardPaths) || !source.wildcardPaths.length)) {
        if(datasetSettings.fileName && datasetSettings.fileName.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileName,
            value: datasetSettings.fileName.expression
          });
        }
        if(datasetSettings.fileMode){
          dslConnectorObjects.push({
            name: DslConnectorConstants.fileMode,
            value: datasetSettings.fileMode
          });
        }
        if(datasetSettings.folderPath && datasetSettings.folderPath.expression){
          dslConnectorObjects.push({
            name: DslConnectorConstants.folderPath,
            value: datasetSettings.folderPath.expression
          });
        }
      }
    } else if((source.datasetSettings as OnPremiseSqlServerSettings).getFormat() === DatasetFormatType.OnPremiseSqlServer){
      const datasetSettings = source.datasetSettings as OnPremiseSqlServerSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.OnPremiseSqlServer
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.tableName.expression)
        });
      }
      if(datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.schemaName.expression)
        });
      }
    } else if((source.datasetSettings as SynapseAnalyticsSettings).getFormat() === DatasetFormatType.SynapseAnalytics){
      const datasetSettings = source.datasetSettings as SynapseAnalyticsSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.SynapseAnalytics
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.tableName.expression)
        });
      }
      if(datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.schemaName.expression)
        });
      }
      if(datasetSettings.format){
        dslConnectorObjects.push({
          name: DslConnectorConstants.format,
          value: datasetSettings.format
        });
      }
      dslConnectorObjects.push({
        name: DslConnectorConstants.database,
        value: 'dummy'
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.databaseType,
        value: 'dummy'
      });
    } else if((source.datasetSettings as DeltaSettings).getFormat() === DatasetFormatType.Delta){
      const datasetSettings = source.datasetSettings as DeltaSettings;

      dslConnectorObjects.push({
        name: DslConnectorConstants.format,
        value: DatasetFormatType.Delta
      });

      if(datasetSettings.container && datasetSettings.container.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.container,
          value: datasetSettings.container.expression
        });
      }
      if(datasetSettings.folderPath && datasetSettings.folderPath.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.folderPath,
          value: datasetSettings.folderPath.expression
        });
      }
    } else if((source.datasetSettings as SqlDBSettings).getFormat() === DatasetFormatType.SqlDB){
      const datasetSettings = source.datasetSettings as SqlDBSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.SqlDB
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.tableName.expression)
        });
      }
      if(datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.schemaName.expression)
        });
      }
    } else if((source.datasetSettings as SqlMISettings).getFormat() === DatasetFormatType.SqlMI){
      const datasetSettings = source.datasetSettings as SqlMISettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.SqlMI
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.tableName.expression)
        });
      }
      if(datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.schemaName.expression)
        });
      }
    } else if((source.datasetSettings as EventHubsSettings).getFormat() === DatasetFormatType.EventHubs){
      const datasetSettings = source.datasetSettings as EventHubsSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: 'eventhubs'
      });
      if(!!datasetSettings.eventHubsName?.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.eventHubsName,
          value: datasetSettings.eventHubsName.expression
        });
      }
      if(!!datasetSettings.eventHubsNamePattern?.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.eventHubsNamePattern,
          value: datasetSettings.eventHubsNamePattern.expression
        });
      }
      if(!!datasetSettings.format){
        dslConnectorObjects.push({
          name: DslConnectorConstants.format,
          value: datasetSettings.format.expression
        });
      }
      if(!!datasetSettings.payloadSchema.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.payloadSchema,
          value: datasetSettings.payloadSchema.expression
        });
      }
      if(!!datasetSettings.includeHeaders.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.includeHeaders,
          value: datasetSettings.includeHeaders.expression
        });
      }
      if (!!datasetSettings.format && datasetSettings.format.expression !== DslConnectorConstants.passThrough && !!datasetSettings.isAutoInfer.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaInferenceType,
          value: datasetSettings.isAutoInfer.expression === 'true' ? DslConnectorConstants.autoInferSchema : DslConnectorConstants.payloadSchema
        });
      }
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: 'dummy'
      });
    } else if((source.datasetSettings as SnowflakeSettings).getFormat() === DatasetFormatType.Snowflake){
      const datasetSettings = source.datasetSettings as SnowflakeSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: DatasetFormatType.Snowflake
      });
      if(datasetSettings.tableName && datasetSettings.tableName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.tableName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.tableName.expression)
        });
      }
      if(datasetSettings.schemaName && datasetSettings.schemaName.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaName,
          value: DataFlowExpressionUtils.escapeSingleQuoteInDslString(datasetSettings.schemaName.expression)
        });
      }
      dslConnectorObjects.push({
        name: DslConnectorConstants.database,
        value: 'dummy'
      });
      dslConnectorObjects.push({
        name: DslConnectorConstants.databaseType,
        value: 'dummy'
      });
    }
    else if((source.datasetSettings as KafkaSettings).getFormat() === DatasetFormatType.Kafka){
      const datasetSettings = source.datasetSettings as KafkaSettings;
      dslConnectorObjects.push({
        name: DslConnectorConstants.store,
        value: 'kafka'
      });
      if(!!datasetSettings.subscribe?.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.subscribe,
          value: datasetSettings.subscribe.expression
        });
      }
      if(!!datasetSettings.subscribePattern?.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.subscribePattern,
          value: datasetSettings.subscribePattern.expression
        });
      }
      if(!!datasetSettings.bootstarpsServers){
        dslConnectorObjects.push({
          name: DslConnectorConstants.bootstrapServers,
          value: datasetSettings.bootstarpsServers
        });
      }
      if(!!datasetSettings.format){
        dslConnectorObjects.push({
          name: DslConnectorConstants.format,
          value: datasetSettings.format.expression
        });
      }
      if(!!datasetSettings.payloadSchema.expression){
        dslConnectorObjects.push({
          name: DslConnectorConstants.payloadSchema,
          value: datasetSettings.payloadSchema.expression
        });
      }
      if(!!datasetSettings.includeHeaders){
        dslConnectorObjects.push({
          name: DslConnectorConstants.includeHeaders,
          value: datasetSettings.includeHeaders.expression
        });
      }
      if (!!datasetSettings.format && datasetSettings.format.expression !== DslConnectorConstants.passThrough && !!datasetSettings.isAutoInfer.expression) {
        dslConnectorObjects.push({
          name: DslConnectorConstants.schemaInferenceType,
          value: datasetSettings.isAutoInfer.expression === 'true' ? DslConnectorConstants.autoInferSchema : DslConnectorConstants.payloadSchema
        });
      }
      dslConnectorObjects.push({
        name: DslConnectorConstants.fileSystem,
        value: 'dummy'
      });
    }
    return dslConnectorObjects;
  }
}
